'use client'
import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Listbox } from '@headlessui/react'
import { RiArrowDropDownLine } from 'react-icons/ri'
import { FaSearch } from 'react-icons/fa'

import { categoryTypes } from '@constants/homeCategoryTypes'
import { homeRoomTypes } from '@constants/homeRoomTypes'
import { saleMaxPrices } from '@constants/saleMaxPrice'
import { saleMinPrices } from '@constants/saleMinPrice'
import { rentMaxPrices } from '@constants/rentMaxPrice'
import { rentMinPrices } from '@constants/rentMinPrice'

// ─── RTL languages ────────────────────────────────────────────────────────────

const RTL_LANGS = new Set(['he', 'ar'])

// ─── Default placeholders ─────────────────────────────────────────────────────

const DEFAULT_PLACEHOLDERS = {
  saleTypeDropdown: { en: 'Sale Type',   th: 'ประเภท',        cn: '类型',      ru: 'Тип сделки',  fr: 'Type de vente',  de: 'Angebotstyp',   it: 'Tipo di vendita', ja: '種別',      ko: '유형',      es: 'Tipo de venta',  he: 'סוג עסקה',        ar: 'نوع البيع',        tr: 'İlan Türü'       },
  category:         { en: 'Category',    th: 'ประเภทอสังหาฯ',  cn: '类别',      ru: 'Категория',   fr: 'Catégorie',      de: 'Kategorie',     it: 'Categoria',       ja: 'カテゴリー', ko: '카테고리',  es: 'Categoría',      he: 'קטגוריה',         ar: 'الفئة',            tr: 'Kategori'        },
  categories:       { en: 'Categories',  th: 'ประเภทอสังหาฯ',  cn: '类别',      ru: 'Категории',   fr: 'Catégories',     de: 'Kategorien',    it: 'Categorie',       ja: 'カテゴリー', ko: '카테고리',  es: 'Categorías',     he: 'קטגוריות',        ar: 'الفئات',           tr: 'Kategoriler'     },
  district:         { en: 'District',    th: 'เขต',            cn: '地区',      ru: 'Район',       fr: 'Quartier',       de: 'Bezirk',        it: 'Quartiere',       ja: '地区',      ko: '지역',      es: 'Distrito',       he: 'רובע',            ar: 'الحي',             tr: 'Bölge'           },
  districts:        { en: 'Districts',   th: 'เขต',            cn: '地区',      ru: 'Районы',      fr: 'Quartiers',      de: 'Bezirke',       it: 'Quartieri',       ja: '地区',      ko: '지역',      es: 'Distritos',      he: 'רובעים',          ar: 'الأحياء',          tr: 'Bölgeler'        },
  province:         { en: 'Province',    th: 'จังหวัด',        cn: '省份',      ru: 'Провинция',   fr: 'Province',       de: 'Provinz',       it: 'Provincia',       ja: '県',        ko: '지역(도)',  es: 'Provincia',      he: 'מחוז',            ar: 'المحافظة',         tr: 'İl'              },
  provinces:        { en: 'Provinces',   th: 'จังหวัด',        cn: '省份',      ru: 'Провинции',   fr: 'Provinces',      de: 'Provinzen',     it: 'Province',        ja: '県',        ko: '지역(도)',  es: 'Provincias',     he: 'מחוזות',          ar: 'المحافظات',        tr: 'İller'           },
  bts:              { en: 'BTS / MRT',   th: 'BTS / MRT',      cn: 'BTS / MRT', ru: 'BTS / MRT',   fr: 'BTS / MRT',      de: 'BTS / MRT',     it: 'BTS / MRT',       ja: 'BTS / MRT', ko: 'BTS / MRT', es: 'BTS / MRT',      he: 'BTS / MRT',       ar: 'BTS / MRT',        tr: 'BTS / MRT'       },
  bedrooms:         { en: 'Bedrooms',    th: 'ห้องนอน',         cn: '卧室',      ru: 'Спальни',     fr: 'Chambres',       de: 'Schlafzimmer',  it: 'Camere',          ja: '寝室',      ko: '침실',      es: 'Habitaciones',   he: 'חדרי שינה',       ar: 'غرف النوم',        tr: 'Yatak Odası'     },
  minPrice:         { en: 'Min Price',   th: 'ราคาต่ำสุด',      cn: '最低价',    ru: 'Мин. цена',   fr: 'Prix min',       de: 'Mindestpreis',  it: 'Prezzo min',      ja: '最低価格',  ko: '최저가',    es: 'Precio mín.',    he: 'מחיר מינימום',    ar: 'أدنى سعر',         tr: 'Min Fiyat'       },
  maxPrice:         { en: 'Max Price',   th: 'ราคาสูงสุด',      cn: '最高价',    ru: 'Макс. цена',  fr: 'Prix max',       de: 'Höchstpreis',   it: 'Prezzo max',      ja: '最高価格',  ko: '최고가',    es: 'Precio máx.',    he: 'מחיר מקסימום',    ar: 'أقصى سعر',         tr: 'Maks Fiyat'      },
  name:             { en: 'Name / Ref…', th: 'ชื่อ / รหัส…',   cn: '名称/编号…', ru: 'Имя / Арт…',  fr: 'Nom / Réf…',     de: 'Name / Ref…',   it: 'Nome / Rif…',     ja: '名前/参照…', ko: '이름/참조…', es: 'Nombre/Ref…',    he: 'שם / מזהה…',      ar: 'الاسم / المرجع…',  tr: 'Ad / Ref…'       },
  searchButton:     { en: 'Search',      th: 'ค้นหา',           cn: '搜索',      ru: 'Поиск',       fr: 'Rechercher',     de: 'Suchen',        it: 'Cerca',           ja: '検索',      ko: '검색',      es: 'Buscar',         he: 'חיפוש',           ar: 'بحث',              tr: 'Ara'             },
}


// ─── URL builder ──────────────────────────────────────────────────────────────

function buildHref({ saleType, categories, districts, provinces, bts, bedrooms, minPrice, maxPrice, name }) {
  if (name?.trim()) return `/properties?name=${encodeURIComponent(name.trim())}`
  const p = [`saleType=${saleType}`]
  const cats  = categories.filter(c => c.value && c.value !== 'all')
  const dists = districts.filter(d => d.id   && d.id   !== 'all')
  const provs = (provinces || []).filter(pr => pr.id && pr.id !== 'all')
  const btss  = bts.filter(b => b.id && b.id !== 'all')
  if (cats.length)  p.push(`categories=${cats.map(c => c.value).join('%2C')}`)
  if (dists.length) p.push(`districts=${dists.map(d => d.id).join('%2C')}`)
  if (provs.length) p.push(`province=${provs.map(pr => pr.id).join('%2C')}`)
  if (btss.length)  p.push(`bts=${btss.map(b => b.id).join('%2C')}`)
  if (bedrooms?.value && bedrooms.value !== '0') p.push(`room=${bedrooms.value}`)
  if (minPrice?.value && minPrice.value !== '0') p.push(`minprice=${minPrice.value}`)
  if (maxPrice?.value && maxPrice.value !== '')  p.push(`maxprice=${maxPrice.value}`)
  return `/properties?${p.join('&')}`
}

// ─── Filter dropdown ──────────────────────────────────────────────────────────

function FilterDropdown({ placeholder, value, options, onChange, getLabel, isMulti, fs, lang }) {
  const isRTL = RTL_LANGS.has(lang)
  const displayText = isMulti
    ? (value.length ? value.map(getLabel).filter(Boolean).join(', ') : placeholder)
    : (getLabel(value) || placeholder)

  const menuBg         = fs.menuBg             || '#ffffff'
  const menuTextColor  = fs.menuTextColor      || '#374151'
  const hoverBg        = fs.hoverBg            || '#f3f4f6'
  const hoverTextColor = fs.hoverTextColor     || '#111111'
  const selectedBg     = fs.selectedBg         || '#eff6ff'
  const selectedText   = fs.selectedTextColor  || '#1d4ed8'
  const selectedWeight = fs.selectedFontWeight || '600'
  const showCheck      = fs.selectedShowCheck  ?? true

  return (
    <Listbox value={value} onChange={onChange} multiple={isMulti}>
      <div className="relative" style={{ flex: 1, minWidth: 0 }}>
        <Listbox.Button
          className="relative w-full cursor-default focus:outline-none"
          style={{
            height:       fs.height       || '38px',
            background:   fs.bg           || '#fff',
            color:        fs.textColor    || '#111',
            fontSize:     fs.fontSize     || '0.875rem',
            borderRadius: fs.borderRadius || '6px',
            border:       `${fs.borderWidth || 1}px solid ${fs.borderColor || '#d1d5db'}`,
            display:      'flex',
            alignItems:   'center',
            direction:    isRTL ? 'rtl' : 'ltr',
            textAlign:    isRTL ? 'right' : 'left',
            paddingLeft:  isRTL ? '28px' : '12px',
            paddingRight: isRTL ? '12px' : '28px',
            overflow:     'hidden',
          }}
        >
          <span style={{
            display:   'block',
            overflow:  'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            color: displayText === placeholder ? (fs.placeholderColor || '#9ca3af') : (fs.textColor || '#111'),
            textAlign: isRTL ? 'right' : 'left',
          }}>
            {displayText}
          </span>
          <span className="pointer-events-none absolute inset-y-0 flex items-center" style={{ [isRTL ? 'left' : 'right']: '4px' }}>
            <RiArrowDropDownLine size={20} style={{ color: fs.chevronColor || fs.textColor || '#666', flexShrink: 0 }} />
          </span>
        </Listbox.Button>
        <Listbox.Options
          className="absolute z-50 mt-1 w-full max-h-56 overflow-auto py-1 text-sm focus:outline-none"
          style={{
            background:   fs.menuBg           || '#ffffff',
            borderRadius: fs.menuBorderRadius || '6px',
            border:       `1px solid ${fs.menuBorderColor || '#e5e7eb'}`,
            boxShadow:    fs.menuShadow       || '0 4px 16px rgba(0,0,0,0.10)',
            direction:    isRTL ? 'rtl' : 'ltr',
          }}
        >
          {(options || []).map((opt, i) => (
            <Listbox.Option key={i} value={opt}>
              {({ active, selected }) => (
                <div style={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between',
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                  padding: '7px 12px', cursor: 'default', userSelect: 'none',
                  fontSize:    fs.fontSize || '0.875rem',
                  background:  selected ? selectedBg : active ? hoverBg : 'transparent',
                  color:       selected ? selectedText : active ? hoverTextColor : menuTextColor,
                  fontWeight:  selected ? selectedWeight : 'normal',
                  textAlign:   isRTL ? 'right' : 'left',
                }}>
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getLabel(opt)}</span>
                  {showCheck && selected && (
                    <span style={{ color: selectedText, flexShrink: 0, [isRTL ? 'marginRight' : 'marginLeft']: 8, fontSize: '0.75rem' }}>✓</span>
                  )}
                </div>
              )}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </div>
    </Listbox>
  )
}

// ─── Name input ───────────────────────────────────────────────────────────────

function NameInput({ value, onChange, fs, inp, placeholder, lang }) {
  const isRTL = RTL_LANGS.has(lang)
  const bg           = inp?.bg           || fs.bg           || '#fff'
  const textColor    = inp?.textColor    || fs.textColor    || '#111'
  const fontSize     = inp?.fontSize     || fs.fontSize     || '0.875rem'
  const borderRadius = inp?.borderRadius || fs.borderRadius || '6px'
  const borderWidth  = inp?.borderWidth  || fs.borderWidth  || 1
  const borderColor  = inp?.borderColor  || fs.borderColor  || '#d1d5db'
  const height       = inp?.height       || fs.height       || '38px'
  const placeholderColor = inp?.placeholderColor || fs.placeholderColor || '#9ca3af'

  return (
    <div className="relative" style={{ flex: 1, minWidth: 0 }}>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Name / Ref…'}
        dir={isRTL ? 'rtl' : 'ltr'}
        className="w-full focus:outline-none"
        style={{
          height, background: bg, color: textColor, fontSize, borderRadius,
          border: `${borderWidth}px solid ${borderColor}`,
          '--placeholder-color': placeholderColor,
          paddingLeft:  isRTL ? '28px' : '12px',
          paddingRight: isRTL ? '12px' : '28px',
          textAlign: isRTL ? 'right' : 'left',
        }}
      />
      {value && (
        <button type="button" onClick={() => onChange('')}
          className="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs leading-none"
          style={{ [isRTL ? 'left' : 'right']: '8px' }}>✕</button>
      )}
    </div>
  )
}

// ─── Sale type tabs ───────────────────────────────────────────────────────────

function SaleTypeTabs({ enabledTabs, activeSaleType, setActiveSaleType, ts, lang, isMobile = false }) {
  const isRTL      = RTL_LANGS.has(lang)
  const isUnderline    = ts.variant === 'underline'
  const fw             = isMobile ? (ts.fullWidthMobile || false) : (ts.fullWidth || false)
  const justifyContent = ts.alignment === 'center' ? 'center' : ts.alignment === 'right' ? 'flex-end' : 'flex-start'
  return (
    <div style={{ display: 'flex', gap: `${ts.gap || 8}px`, flexWrap: fw ? 'nowrap' : 'wrap', width: '100%', justifyContent, direction: isRTL ? 'rtl' : 'ltr' }}>
      {enabledTabs.map(t => {
        const active = activeSaleType === t.value
        return (
          <button key={t.id} onClick={() => setActiveSaleType(t.value)} style={{
            padding:      `${ts.paddingY || 6}px ${ts.paddingX || 16}px`,
            borderRadius: isUnderline ? '0' : (ts.borderRadius || '6px'),
            fontSize:     ts.fontSize   || '0.875rem',
            fontWeight:   ts.fontWeight || '600',
            cursor:       'pointer',
            transition:   'all 0.15s',
            flex:         fw ? '1' : undefined,
            whiteSpace:   'nowrap',
            background: active
              ? (isUnderline ? 'transparent' : (ts.activeBackground   || '#000'))
              : (isUnderline ? 'transparent' : (ts.inactiveBackground || 'transparent')),
            color: active ? (ts.activeTextColor || '#fff') : (ts.inactiveTextColor || '#000'),
            border: ts.variant === 'outline'
              ? `${ts.borderWidth || '1px'} solid ${active ? ts.activeBorderColor : ts.inactiveBorderColor}`
              : 'none',
            borderBottom:  isUnderline ? `2px solid ${active ? ts.activeBackground || '#000' : 'transparent'}` : undefined,
            boxShadow:     ts.boxShadow || 'none',
          }}>
            {t.label?.[lang]}
          </button>
        )
      })}
    </div>
  )
}

// ─── Search button ────────────────────────────────────────────────────────────

function SearchBtn({ href, s2, isMobile, lang, filterState  }) {
  const fs  = s2.filterStyle || {}
  const btn = s2.searchBtn   || {}
  const ph  = s2.placeholders || {}

  const bg           = btn.bg           || s2.searchBtnBg           || '#000'
  const textColor    = btn.textColor    || s2.searchBtnTextColor    || '#fff'
  const borderRadius = btn.borderRadius || s2.searchBtnBorderRadius || '6px'
  const fontWeight   = btn.fontWeight   || '600'
  const fontSize     = btn.fontSize     || fs.fontSize              || '0.875rem'
  const paddingX     = btn.paddingX     || '20'
  const shadow       = btn.shadow       || 'none'
  const borderWidth  = parseInt(btn.borderWidth) || 0
  const borderColor  = btn.borderColor  || 'transparent'
  const label        =
    ph.searchButton?.[lang]
    || DEFAULT_PLACEHOLDERS.searchButton?.[lang]
    || 'Search'
  const showIcon     = btn.showIcon     ?? s2.searchBtnShowIcon     ?? true
  const showLabel    = btn.showLabel    ?? s2.searchBtnShowLabel    ?? true
  const fullWidthM   = btn.fullWidthMobile ?? s2.searchBtnFullWidthMobile ?? false
  const iconOnly     = showIcon && !showLabel
  const isRTL        = RTL_LANGS.has(lang)

  const handleClick = async () => {
    const { sendGAEvent } = await import('@next/third-parties/google')
    sendGAEvent('event', 'home_search_clicked', {
      search_sale_type: filterState.saleType,
      search_categories: filterState.categories.map(c => c.value).join(',') || null,
      search_districts: filterState.districts.map(d => d.id).join(',') || null,
      search_districts_count: filterState.districts.length || null,
      search_provinces: filterState.provinces.map(p => p.id).join(',') || null,
      search_provinces_count: filterState.provinces.length || null,
      search_bts: filterState.bts.map(b => b.id).join(',') || null,
      search_bts_count: filterState.bts.length || null,
      search_bedrooms: filterState.bedrooms?.value || null,
      search_min_price: filterState.minPrice?.value || null,
      search_max_price: filterState.maxPrice?.value || null,
      search_name_query: filterState.name.trim() || null,
      search_filter_count: [
        filterState.categories.length > 0,
        filterState.districts.length > 0,
        filterState.provinces.length > 0,
        filterState.bts.length > 0,
        !!filterState.bedrooms,
        !!filterState.minPrice,
        !!filterState.maxPrice,
        !!filterState.name.trim(),
      ].filter(Boolean).length,
    })
  }

  return (
    <Link href={href} onClick={handleClick} style={{
      display:         'inline-flex',
      alignItems:      'center',
      justifyContent:  'center',
      flexDirection:   isRTL ? 'row-reverse' : 'row',
      gap:             showIcon && showLabel ? 6 : 0,
      width:           isMobile && fullWidthM ? '100%' : iconOnly ? (fs.height || '38px') : undefined,
      padding:         iconOnly ? '0' : `0 ${paddingX}px`,
      height:          fs.height || '38px',
      background:      bg,
      color:           textColor,
      borderRadius,
      border:          borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : 'none',
      fontSize,
      fontWeight,
      textDecoration:  'none',
      whiteSpace:      'nowrap',
      flexShrink:      0,
      boxSizing:       'border-box',
      cursor:          'pointer',
      boxShadow:       shadow,
    }}>
      {showIcon  && <FaSearch size={13} />}
      {showLabel && <span>{label}</span>}
    </Link>
  )
}

// ─── Filter row ───────────────────────────────────────────────────────────────

function FilterRow({ row, filterState, setFilterState, enabledTabs, ts, districtList, provinceList, btsList, lang, s2, isMobile }) {
  const isRTL = RTL_LANGS.has(lang)
  const fs  = s2.filterStyle || {}
  const inp = s2.inputStyle  || {}
  const ph  = s2.placeholders || {}
  const pl  = (key) => ph[key]?.[lang] || DEFAULT_PLACEHOLDERS[key]?.[lang] || key

  const { saleType, category, categories, district, districts, province, provinces, bts, bedrooms, minPrice, maxPrice, name } = filterState
  const rentMode    = saleType === 'rent'
  const searchHref  = buildHref({ saleType, categories, districts, provinces, bts, bedrooms, minPrice, maxPrice, name })
  const saleTypeOptions = enabledTabs.map(t => ({ value: t.value, label: t.label?.[lang] || t.value }))

  // Cascading filter: once a REAL province is picked, only show districts
  // that belong to it. The "All Provinces" placeholder entry has id: '',
  // so it's filtered out here — otherwise it would count as a truthy
  // selection and narrow districts down to location === '' (i.e. none).
  // Assumes each district object carries the same province key its parent
  // /activeprovinces entry used (`district.location` matching `province.id`).
  const selectedProvinceIds = [
    ...(provinces || []),
    ...(province ? [province] : []),
  ]
    .filter(pr => pr?.id)
    .map(pr => pr.id)

  const activeProvinceIds = selectedProvinceIds.length ? selectedProvinceIds : null

  const filteredDistrictList = activeProvinceIds
    ? (districtList || []).filter(d => activeProvinceIds.includes(d.location))
    : districtList

  return (
    <div style={{ display: 'flex', gap: `${row.gap ?? 8}px`, flexWrap: row.wrap ? 'wrap' : 'nowrap', justifyContent: row.justify || 'flex-start', alignItems: 'center', direction: isRTL ? 'rtl' : 'ltr' }}>
      {(row.slots || []).map((slot, si) => {
        if (slot === 'saleType') return (
          <div key={si} style={{ width: '100%' }}>
            <SaleTypeTabs enabledTabs={enabledTabs} activeSaleType={saleType} setActiveSaleType={v => setFilterState(p => ({ ...p, saleType: v }))} ts={ts} lang={lang} isMobile={isMobile} />
          </div>
        )
        if (slot === 'saleTypeDropdown') return (
          <FilterDropdown key={si} placeholder={pl('saleTypeDropdown')} value={saleTypeOptions.find(o => o.value === saleType) || null} options={saleTypeOptions} onChange={v => setFilterState(p => ({ ...p, saleType: v?.value || p.saleType }))} getLabel={v => v?.label || ''} isMulti={false} fs={fs} lang={lang} />
        )
        if (slot === 'category') return (
          <FilterDropdown key={si} placeholder={pl('category')} value={category} options={categoryTypes?.[lang] || []} onChange={v => setFilterState(p => ({ ...p, category: v, categories: v ? [v] : [] }))} getLabel={v => v?.title || ''} isMulti={false} fs={fs} lang={lang} />
        )
        if (slot === 'categories') return (
          <FilterDropdown key={si} placeholder={pl('categories')} value={categories} options={categoryTypes?.[lang] || []} onChange={v => setFilterState(p => ({ ...p, categories: v.filter(c => c.value !== 'all'), category: v[0] || null }))} getLabel={v => v?.title || ''} isMulti fs={fs} lang={lang} />
        )
        if (slot === 'district') return (
          <FilterDropdown key={si} placeholder={pl('district')} value={district} options={filteredDistrictList || []} onChange={v => setFilterState(p => ({ ...p, district: v, districts: v ? [v] : [] }))} getLabel={v => v?.subdistrict || ''} isMulti={false} fs={fs} lang={lang} />
        )
        if (slot === 'districts') return (
          <FilterDropdown key={si} placeholder={pl('districts')} value={districts} options={filteredDistrictList || []} onChange={v => setFilterState(p => ({ ...p, districts: v.filter(d => d.id !== 'all'), district: v[0] || null }))} getLabel={v => v?.subdistrict || ''} isMulti fs={fs} lang={lang} />
        )
        if (slot === 'province') return (
          <FilterDropdown key={si} placeholder={pl('province')} value={province} options={provinceList || []} onChange={v => setFilterState(p => ({ ...p, province: v?.id ? v : null, provinces: v?.id ? [v] : [] }))} getLabel={v => v?.province || ''} isMulti={false} fs={fs} lang={lang} />
        )
        if (slot === 'provinces') return (
          <FilterDropdown key={si} placeholder={pl('provinces')} value={provinces} options={provinceList || []} onChange={v => { const real = v.filter(pr => pr.id); setFilterState(p => ({ ...p, provinces: real, province: real[0] || null })) }} getLabel={v => v?.province || ''} isMulti fs={fs} lang={lang} />
        )
        if (slot === 'bts') return (
          <FilterDropdown key={si} placeholder={pl('bts')} value={bts} options={btsList || []} onChange={v => setFilterState(p => ({ ...p, bts: v.filter(b => b.id !== 'all') }))} getLabel={v => v?.stationName || v?.station || v?.id || ''} isMulti fs={fs} lang={lang} />
        )
        if (slot === 'bedrooms') return (
          <FilterDropdown key={si} placeholder={pl('bedrooms')} value={bedrooms} options={homeRoomTypes?.[lang] || []} onChange={v => setFilterState(p => ({ ...p, bedrooms: v }))} getLabel={v => v?.title || ''} isMulti={false} fs={fs} lang={lang} />
        )
        if (slot === 'minPrice') return (
          <FilterDropdown key={si} placeholder={pl('minPrice')} value={minPrice} options={(rentMode ? rentMinPrices : saleMinPrices)?.[lang] || []} onChange={v => setFilterState(p => ({ ...p, minPrice: v }))} getLabel={v => v?.nom || ''} isMulti={false} fs={fs} lang={lang} />
        )
        if (slot === 'maxPrice') return (
          <FilterDropdown key={si} placeholder={pl('maxPrice')} value={maxPrice} options={(rentMode ? rentMaxPrices : saleMaxPrices)?.[lang] || []} onChange={v => setFilterState(p => ({ ...p, maxPrice: v }))} getLabel={v => v?.nom || ''} isMulti={false} fs={fs} lang={lang} />
        )
        if (slot === 'name') return (
          <NameInput key={si} value={name} onChange={v => setFilterState(p => ({ ...p, name: v }))} fs={fs} inp={inp} placeholder={pl('name')} lang={lang} />
        )
        if (slot === 'searchButton') return (
          <SearchBtn key={si} href={searchHref} s2={s2} isMobile={isMobile} lang={lang} filterState={filterState}/>
        )
        return null
      })}
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function FrontSearchBarStyle2({ homesearch, lang, districtList, provinceList, btsList }) {
  const values = typeof homesearch === 'string' ? JSON.parse(homesearch) : homesearch
  if (!values) return null

  const isRTL       = RTL_LANGS.has(lang)
  const s2          = values.style2Layout || {}
  const ts          = values.saleTypeTabsStyle || {}
  const enabledTabs = (values.saleTypes || []).filter(t => t.enabled)
  const firstSaleType = enabledTabs[0]?.value || 'sale'

  const [filterState, setFilterState] = useState({
    saleType: firstSaleType, category: null, categories: [],
    district: null, districts: [],
    province: null, provinces: [],
    bts: [],
    bedrooms: null, minPrice: null, maxPrice: null, name: '',
  })

  useEffect(() => {
    setFilterState(p => ({ ...p, minPrice: null, maxPrice: null }))
  }, [filterState.saleType, lang])

  // When the province filter changes, drop any selected district(s) that no
  // longer belong to the new province set (or clear entirely if province
  // was cleared back to "All Provinces" — safest default, avoids a silently
  // stale district scoping a search the user thinks is province-wide).
  useEffect(() => {
    const selectedProvinceIds = [
      ...(filterState.provinces || []),
      ...(filterState.province ? [filterState.province] : []),
    ]
      .filter(pr => pr?.id)
      .map(pr => pr.id)

    const activeProvinceIds = selectedProvinceIds.length ? selectedProvinceIds : null

    if (!activeProvinceIds) return

    setFilterState(p => {
      const stillValidDistrict  = p.district && activeProvinceIds.includes(p.district.location) ? p.district : null
      const stillValidDistricts = (p.districts || []).filter(d => activeProvinceIds.includes(d.location))
      if (stillValidDistrict === p.district && stillValidDistricts.length === (p.districts || []).length) {
        return p // nothing changed, avoid extra re-render
      }
      return { ...p, district: stillValidDistrict, districts: stillValidDistricts }
    })
  }, [filterState.province, filterState.provinces])

  const desktopRows = s2.desktopRows || []
  const mobileRows  = s2.mobileRows  || []
  const sharedProps = { filterState, setFilterState, enabledTabs, ts, districtList, provinceList, btsList, lang, s2 }

  const panelStyle = (padding) => ({
    background:   values.backgroundColor || '#fff',
    border:       `1px solid ${values.borderColor || '#e5e7eb'}`,
    boxShadow:    values.boxShadow || 'none',
    borderRadius: values.panelBorderRadius || '8px',
    padding,
    display:       'flex',
    flexDirection: 'column',
    gap:           `${s2.rowGap ?? 8}px`,
    direction:     isRTL ? 'rtl' : 'ltr',
  })

  const header = (
    <>
      {values.slogan?.[lang] && (
        <h2 style={{ fontSize: values.sloganStyle?.fontSize, fontWeight: values.sloganStyle?.fontWeight, textAlign: isRTL ? (values.sloganStyle?.textAlign === 'left' ? 'right' : values.sloganStyle?.textAlign === 'right' ? 'left' : values.sloganStyle?.textAlign) : values.sloganStyle?.textAlign, color: values.sloganStyle?.color, margin: 0 }}>
          {values.slogan[lang]}
        </h2>
      )}
      {values.description?.[lang] && (
        <p style={{ fontSize: values.descriptionStyle?.fontSize, fontWeight: values.descriptionStyle?.fontWeight, textAlign: isRTL ? (values.descriptionStyle?.textAlign === 'left' ? 'right' : values.descriptionStyle?.textAlign === 'right' ? 'left' : values.descriptionStyle?.textAlign) : values.descriptionStyle?.textAlign, color: values.descriptionStyle?.color, margin: 0 }}>
          {values.description[lang]}
        </p>
      )}
    </>
  )

  return (
    <>
      {/* ── Desktop ── */}
      <div className="md:animate-slide-in-bottom hidden md:flex absolute w-full z-40 md:bottom-[50px] flex-col items-center px-4">
        <div style={{ width: '100%', maxWidth: values.widthDesktop || 800 }}>
          <div style={panelStyle('16px')}>
            {header}
            {desktopRows.map((row, i) => (
              <FilterRow key={row.id || i} row={row} isMobile={false} {...sharedProps} />
            ))}
          </div>
        </div>
      </div>

      {/* ── Mobile ── */}
      <div className="block md:hidden w-full px-4 py-3" style={{ position: 'absolute', bottom: '78px', left: 0, right: 0, zIndex: 40 }}>
        <div style={{ maxWidth: values.widthMobile || 380, margin: '0 auto' }}>
          <div style={panelStyle('12px')}>
            {header}
            {mobileRows.map((row, i) => (
              <FilterRow key={row.id || i} row={row} isMobile={true} {...sharedProps} />
            ))}
          </div>
        </div>
      </div>
    </>
  )
}